<template>
  <template v-if="seminarDetails">
    <div class="card-outlined p-4">
      <base-alert class="mb-4" />
      <form @submit="onSubmit">
        <template v-if="seminarStatus && seminarDetails.seminar_category_id === 1">
          <div class="mb-4 mt-4">
            <label
              for="status"
              class="form-label"
            >
              参加方法
            </label>
            <base-form-select class="mb-1" v-model:selectedVal="seminar_status_id" :options="seminarStatus" />
            <p class="form-error-text">{{ errors['seminar_status_id'] }}</p>
          </div>
        </template>
        <div class="mb-4">
          <label
            for="name"
            class="form-label"
          >
            {{ t('word.name') }}
          </label>
          <input
            v-model="name"
            id="name"
            name="name"
            type="text"
            :placeholder="t('word.name')"
            :class="`form-input mb-1 ${errors['name'] ? 'border-red-500' : ''}`"
          >
          <p class="form-error-text">{{ errors['name'] }}</p>
        </div>
        <div class="mb-4">
          <label
            for="email"
            class="form-label"
          >
            {{ t('word.email') }}
          </label>
          <input
            v-model="email"
            id="email"
            name="email"
            type="email"
            :placeholder="t('word.email')"
            :class="`form-input mb-1 ${errors['email'] ? 'border-red-500' : ''}`"
          >
          <p class="form-error-text">{{ errors['email'] }}</p>
        </div>
        <div v-if="seminarDetails.memo1_title" class="mb-4">
          <label
            for="memo1"
            class="form-label"
          >
            {{ seminarDetails.memo1_title }}
          </label>
          <textarea
            v-model="memo1"
            id="memo1"
            name="memo1"
            :placeholder="seminarDetails.memo1_title"
            :class="`form-input mb-1 ${errors['memo1'] ? 'border-red-500' : ''}`"
            :rows="5"
          ></textarea>
          <p class="form-error-text">{{ errors['memo1'] }}</p>
        </div>
        <div v-if="seminarDetails.memo2_title" class="mb-4">
          <label
            for="memo2"
            class="form-label"
          >
            {{ seminarDetails.memo2_title }}
          </label>
          <textarea
            v-model="memo2"
            id="memo2"
            name="memo2"
            :placeholder="seminarDetails.memo2_title"
            :class="`form-input mb-1 ${errors['memo2'] ? 'border-red-500' : ''}`"
            :rows="5"
          ></textarea>
          <p class="form-error-text">{{ errors['memo2'] }}</p>
        </div>
        <div class="pt-4 pb-2 flex items-center justify-between">
          <base-form-submit :disabled="btnDisabled" :text="t('seminar_details.apply.submit')" />
        </div>
      </form>
    </div>
  </template>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, PropType } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useField, useForm } from 'vee-validate'
import * as yup from 'yup'
import { useStore } from '@/store/index'
import { axios, ApiResponse } from '@/plugins/axios'
import BaseFormSubmit from '@/components/BaseFormSubmit.vue'
import BaseAlert from '@/components/BaseAlert.vue'
import { SelectOptionItem } from '@/types/forms/select'
import { SeminarAttributes } from '@/types/models/seminar'
import BaseFormSelect from '@/components/BaseFormSelect.vue'

type SeminarApplyFormValues = {
  seminar_status_id: number
  name: string
  email: string
  memo1: string
  memo2: string
}

export default defineComponent({
  props: {
    seminarDetails: {
      type: Object as PropType<SeminarAttributes | undefined>,
      'default': () => (undefined),
    }
  },
  components: { BaseFormSubmit, BaseAlert, BaseFormSelect },
  setup(props) {
    const { t } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const btnDisabled = ref(false)
    const seminarStatus = ref()
    const initialValues: SeminarApplyFormValues = {
      seminar_status_id: 0,
      name: store.state.user.user?.name ?? '',
      email: store.state.user.user?.email ?? '',
      memo1: '',
      memo2: '',
    }

    const schema = yup.object({
      seminar_status_id: props.seminarDetails && props.seminarDetails.seminar_category_id === 1 ? yup.number().required().min(1, '選択してください') : yup.number(),
      name: yup.string().required().max(255),
      email: yup.string().required().email().max(255),
      memo1: yup.string().max(5000),
      memo2: yup.string().max(5000),
    })

    const { handleSubmit, errors } = useForm<SeminarApplyFormValues>({
      validationSchema: schema,
      initialValues,
    })

    const { value: seminar_status_id } = useField<number>('seminar_status_id')
    const { value: name } = useField<string>('name')
    const { value: email } = useField<string>('email')
    const { value: memo1 } = useField<string>('memo1')
    const { value: memo2 } = useField<string>('memo2')

    const onSubmit = handleSubmit(values => {
      btnDisabled.value = true
      axios
        .post(`/seminar/apply/${route.params.seminar_id}`, values)
        .then(response => {
          store.commit('seminar/setSeminarDetails', response.data.payload)
          router.push(route.path)
        })
        .finally(() => btnDisabled.value = false)
    })

    onMounted(() => {
      if (props.seminarDetails && props.seminarDetails.seminar_category_id === 1) {
        const seminarStatusIds = props.seminarDetails.seminar_status_ids ? props.seminarDetails.seminar_status_ids.split(',').map(Number) : [0]
        axios.get<ApiResponse<SelectOptionItem[]>>('/seminar/status')
          .then(response => {
            if (Array.isArray(response.data.payload)) {
              seminarStatus.value = response.data.payload.filter(item => {
                return seminarStatusIds.includes(Number(item.value))
              })
              seminarStatus.value.unshift({text: '選択してください', value: 0})
            }
          })
      }
    })

    return {
      t,
      errors,
      seminar_status_id,
      name,
      email,
      memo1,
      memo2,
      onSubmit,
      btnDisabled,
      seminarStatus
    }
  },
})
</script>
